import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PLAYBOOK_TOPICS } from '../data/businessPlaybookContent';
import SmartSearch from '../components/shared/SmartSearch';
import TopicCard from '../components/business-playbook/TopicCard';

const BusinessPlaybook = () => {
  const navigate = useNavigate();

  // Flatten all Playbook sections into SmartSearch-compatible articles
  const searchArticles = useMemo(() => {
    const articles = [];
    for (const topic of PLAYBOOK_TOPICS) {
      for (const section of topic.sections) {
        articles.push({
          id: section.id,
          title: section.title,
          content: section.content,
          categoryName: topic.title,
          categoryColor: '#F3E8FF',
          categoryTextColor: '#7C3AED',
          audienceLabel: 'Playbook',
          // For navigation
          _topicSlug: topic.slug,
          _sectionSlug: section.slug,
        });
      }
    }
    return articles;
  }, []);

  const handleSearchResultClick = (article) => {
    navigate(`/business-playbook/${article._topicSlug}/${article._sectionSlug}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Navigation */}
        <span
          role="button"
          tabIndex={0}
          onClick={() => navigate('/dashboard/practitioner')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/practitioner')}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors mb-6 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </span>

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Business Playbook
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto mb-8">
            Everything you need to build a thriving practice
          </p>

          {/* SmartSearch */}
          <SmartSearch
            articles={searchArticles}
            onResultClick={handleSearchResultClick}
            placeholder="Search the Playbook..."
          />
        </div>

        {/* Topics Grid */}
        {PLAYBOOK_TOPICS.length > 0 ? (
          <>
            {/* Main grid — first 6 topics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {PLAYBOOK_TOPICS.slice(0, 6).map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </div>

            {/* 7th card — centered standalone */}
            {PLAYBOOK_TOPICS.length > 6 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px', maxWidth: '1152px', margin: '24px auto 0' }}>
                <div style={{ width: 'calc(33.333% - 16px)', minWidth: '280px', maxWidth: '360px' }}>
                  {PLAYBOOK_TOPICS.slice(6).map((topic) => (
                    <TopicCard key={topic.id} topic={topic} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No topics available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessPlaybook;
